import { startCase } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { SnPasswordAPI } from '@lib/api/services';
import { snFadeInDown } from '@lib/common/animations';
import { TranslationService } from '@lib/translation';

@Component({
  selector: 'sn-reset-confirm',
  templateUrl: './reset-confirm.view.html',
  styleUrls: ['./reset-confirm.view.scss'],
  animations: [snFadeInDown],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetConfirmView implements OnDestroy, OnInit {
  public readonly form: UntypedFormGroup;
  public readonly formModel: any;
  public phoneConfirmationRequired: boolean;

  public isLoading = false;
  public translationsLoaded = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _passwordAPI: SnPasswordAPI,
    private readonly _snackbar: MatSnackBar,
    private readonly _translationService: TranslationService,
  ) {
    this.form = new UntypedFormGroup({});
    this.formModel = {};
  }

  get rules(): string[] {
    return this._passwordAPI.rules;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public ngOnInit(): void {
    this._translationService.translationReady$().pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.translationsLoaded = true;
      this._changeDetectorRef.detectChanges();
    });

    const { uid, token } = this._activatedRoute.snapshot.params;
    this._passwordAPI
      .resetPasswordToken({ uid, token })
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: any) => {
        this.phoneConfirmationRequired = res.phoneConfirmationRequired;
        this._changeDetectorRef.markForCheck();
      });
  }

  public onFormSubmit(): void {
    this.isLoading = true;
    this._changeDetectorRef.markForCheck();

    const { uid, token } = this._activatedRoute.snapshot.params;

    this._passwordAPI
      .resetPasswordConfirm({ uid, token, ...this.form.value })
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe({
        next: this._onSuccess.bind(this),
        error: this._onError.bind(this),
      });
  }

  public toStartCase(text: string): string {
    return startCase(text);
  }

  private _onSuccess({ email }: any): void {
    this._snackbar.open('Password reset successful');
    this._router.navigate(['/auth', 'sign-in'], {
      queryParams: { email },
    });
  }

  private _onError(errorResponse: HttpErrorResponse): void {
    this.form.setErrors(errorResponse.error);
  }
}
